/*
 * Routing primitives. Every push records where it came from
 * (`state.from`, the pathname and search of the entry it leaves), and every
 * replace keeps the `from` of the entry it replaces, so a page can tell
 * whether the entry just behind it is a given page (`useBookNavigation` in
 * `lib/bookNavigation.ts`). Route changes turn the page instantly: no view
 * transition.
 */

import { useCallback, useEffect, useRef, type Ref } from 'react';
import {
  Link as RouterLink,
  NavLink as RouterNavLink,
  useLocation,
  useNavigate as useRouterNavigate,
  type LinkProps,
  type Location,
  type NavigateOptions,
  type NavLinkProps,
  type To,
} from 'react-router';

/** What this app keeps in `history.state.usr`. */
export interface BookState {
  /** The pathname and search of the entry this one was pushed from. */
  from?: string;
  /** Set on an entry a voice turn made. */
  byVoice?: boolean;
  /** The section the reader came up from, to land on its row. */
  land?: { section: string };
}

export const bookState = (location: Pick<Location, 'state'>): BookState =>
  location.state && typeof location.state === 'object' ? (location.state as BookState) : {};

const here = (location: Pick<Location, 'pathname' | 'search'>): string => `${location.pathname}${location.search}`;

/** The state a move writes: a push records the page it leaves, a replace keeps what the replaced entry recorded. */
export function stateFor(location: Pick<Location, 'pathname' | 'search' | 'state'>, replace: boolean | undefined, state: unknown): unknown {
  const from = replace ? bookState(location).from : here(location);
  if (from === undefined) return state;
  if (state === undefined || state === null) return { from };
  return typeof state === 'object' && !Array.isArray(state) ? { from, ...state } : state;
}

export function Link({ state, replace, ref, ...rest }: LinkProps & { ref?: Ref<HTMLAnchorElement> }) {
  const location = useLocation();
  return <RouterLink ref={ref} replace={replace} state={stateFor(location, replace, state)} {...rest} />;
}

export function NavLink({ state, replace, ref, ...rest }: NavLinkProps & { ref?: Ref<HTMLAnchorElement> }) {
  const location = useLocation();
  return <RouterNavLink ref={ref} replace={replace} state={stateFor(location, replace, state)} {...rest} />;
}

export function useNavigate() {
  const navigate = useRouterNavigate();
  const location = useLocation();
  const locationRef = useRef(location);
  locationRef.current = location;
  return useCallback(
    (to: To | number, options?: NavigateOptions) => {
      if (typeof to === 'number') void navigate(to);
      else void navigate(to, { ...options, state: stateFor(locationRef.current, options?.replace, options?.state) });
    },
    [navigate],
  );
}

/** Replaces this entry with `to` once mounted, keeping where the entry came from. */
export function Navigate({ to, state }: { to: To; state?: unknown }) {
  const navigate = useNavigate();
  const target = typeof to === 'string' ? to : JSON.stringify(to);
  useEffect(() => {
    navigate(to, { replace: true, state });
    // `to` is compared by value; a new object with the same target is the same move.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [target]);
  return null;
}
